import * as React from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';

import { defaultLocale } from '../config/i18n.config';
import { LocaleProvider } from '../context/localeContext';
import IndexPage from './IndexPage';
import Layout from '../components/Layout';
import Section from '../components/Section';
import BlogRoll from '../components/BlogRoll';
import PreviewCompatibleImage from '../components/PreviewCompatibleImage';

const IndexTemplate = ({ data, pageContext }) => {
  const image = data.indexPage.frontmatter[defaultLocale].image;
  const hero = data.indexPage.frontmatter[defaultLocale].hero;
  const researchPublications = data.researchPublications.edges;
  const blogPosts = data.blogPosts.edges;
  const funders = data.indexPage.frontmatter[defaultLocale].funders || [];

  return (
    <LocaleProvider locale={pageContext.locale}>
      <Layout>
        <IndexPage heroImage={image} heroContent={hero} />
        {!!researchPublications?.length && (
          <Section overlaySide="left" colorScheme="grey">
            <div className="container py-12">
              <div className="row flex flex-col md:flex-row pb-2 gap-x-36">
                <div className="content-between text-base">
                  <h2 className="mb-6 md:mb-12">
                    <span className="bg-theme-underline">Research</span>
                  </h2>
                  <p className="text-lg">
                    We’re a data-driven organisation. Explore our latest research into disinformation and its harms.
                  </p>
                </div>
              </div>
              <div className="row">
                <BlogRoll blogPosts={researchPublications} />
              </div>
              <div className="row text-center pt-8">
                <Link to="/research" className="button inline-block px-12 py-3 rounded-full bg-primary text-white hover:bg-primaryHover focus:bg-primaryPressed">
                  View all
                </Link>
              </div>
            </div>
          </Section>
        )}
        {!!blogPosts?.length && (
          <Section overlaySide="left" colorScheme="grey">
            <div className="container py-12">
              <div className="row flex flex-col md:flex-row pb-2 gap-x-36">
                <div className="content-between text-base">
                  <h2 className="mb-6 md:mb-12">
                    <span className="bg-theme-underline">Blog</span>
                  </h2>
                  <p className="text-lg">
                    We advocate for a safer information ecosystem. Read our latest insights and updates on the blog.
                  </p>
                </div>
              </div>
              <div className="row">
                <BlogRoll blogPosts={blogPosts} />
              </div>
              <div className="row text-center pt-8">
                <Link to="/blog" className="button inline-block px-12 py-3 rounded-full bg-primary text-white hover:bg-primaryHover focus:bg-primaryPressed">
                  View all
                </Link>
              </div>
            </div>
          </Section>
        )}
        {!!funders?.length && (
          <Section colorScheme="white">
            <div className="container py-16 text-center">
              <h2 className="text-2xl font-bold mb-8">Who Funds Us</h2>
              <div className="flex justify-center items-center gap-12 flex-wrap">
                {funders.map(({ image, alt }) => (
                  <div
                    key={alt}
                    className="transition-all duration-300"
                    style={{ filter: 'grayscale(100%)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.filter = 'grayscale(0%)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = 'grayscale(100%)';
                    }}
                  >
                    <PreviewCompatibleImage
                      imageInfo={{ image, alt }}
                      style={{ maxHeight: '80px', width: 'auto' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}
      </Layout>
    </LocaleProvider>
  );
};

IndexTemplate.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.object,
    }),
  }),
};

export default IndexTemplate;

export const query = graphql`
  query IndexPage($id: String!, $locale: String!) {
    indexPage: markdownRemark(id: { eq: $id }) {
      frontmatter {
        en {
          image {
            childImageSharp {
              gatsbyImageData(
                quality: 100
                placeholder: BLURRED
                layout: FULL_WIDTH
              )
            }
          }
          title
          hero
          funders {
            image {
              childImageSharp {
                gatsbyImageData(
                  height: 80
                  placeholder: BLURRED
                  layout: CONSTRAINED
                )
              }
            }
            alt
          }
        }
        de {
          title
          hero
        }
      }
    }
    researchPublications: allMarkdownRemark(
      sort: { order: DESC, fields: [frontmatter___date] }
      filter: {
        fields: {
          collection: { eq: "research-publications" }
          locale: { eq: $locale }
        }
        frontmatter: { title: { ne: "" }, featured: { eq: true } }
        rawMarkdownBody: { ne: "" }
      }
      limit: 3
    ) {
      edges {
        node {
          ...BlogRollItem
        }
      }
    }
    countryStudies: allMarkdownRemark(
      sort: { order: DESC, fields: [frontmatter___date] }
      filter: {
        fields: {
          collection: { eq: "country-studies" }
          locale: { eq: $locale }
        }
        frontmatter: { title: { ne: "" }, featured: { eq: true } }
        rawMarkdownBody: { ne: "" }
      }
      limit: 3
    ) {
      edges {
        node {
          ...BlogRollItem
        }
      }
    }
    blogPosts: allMarkdownRemark(
      sort: { order: DESC, fields: [frontmatter___date] }
      filter: {
        fields: { collection: { eq: "blog-posts" }, locale: { eq: $locale } }
        frontmatter: { title: { ne: "" }, featured: { eq: true } }
        rawMarkdownBody: { ne: "" }
      }
      limit: 3
    ) {
      edges {
        node {
          ...BlogRollItem
        }
      }
    }
  }
`;
